import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useSearchParams } from "react-router-dom";
import ListPage from "../../components/ListPage";
import { LEGACY_DEFAULT_PAGE_SIZE } from "../../constants/pagination";
import useDeliveries from "../hooks/useDeliveries";
import useDeliveryActions from "../hooks/useDeliveryActions";
import {
    QUEUE_TABS,
    COD_TABS,
    StatusPill,
    CodChip,
    NamePhoneCell,
    partnerName,
    isCodDelivery,
    codAmount,
    deliveryActions,
    formatCurrency,
    LogisticsSearchInput,
    PARTNER_OPTIONS,
    isAwbPending,
} from "./shared";
import { formatShortDate } from "../../utils/format";
import { useAddModalMain } from "../../../contexts/address-modal/AddModal";
import BoxImagesModal from "./BoxImagesModal";
import AddBoxImagesModal from "./AddBoxImagesModal";
import DeliveryExtraItemsModal from "./ExtraItemsModal";
import DeliveryTrackingModal from "../../../components/delivery/DeliveryTrackingModal";
import TrackingDetailsModal from "./TrackingDetailsModal";
import BulkMarkDeliveredModal from "../../../pages/delivery/BulkMarkDeliveredModal";

// The Delivery Queue, and — with `codOnly` — COD Watch.
//
// ONE component for both, because the product owner's spec for COD Watch is "same as
// Delivery Queue, just exclusive for isCOD" (spec §7). Two copies of this page would be two
// places for the column set and the action rules to drift.
//
// Page size is LEGACY_DEFAULT_PAGE_SIZE (50), not the v2 default of 20: this console is a
// restyle of /delivery, which has always paged at 50, and the operators working the queue are
// used to that. See src/v2/constants/pagination.js.

const PARAM = { page: "page", tab: "tab", search: "search", partner: "partner" };

function DeliveryQueuePage({ codOnly }) {
    const [searchParams, setSearchParams] = useSearchParams();
    const page = Math.max(Number(searchParams.get(PARAM.page)) || 1, 1);
    const search = searchParams.get(PARAM.search) || "";
    // Only a known partner id is honoured, so an edited ?partner= cannot send junk to the API.
    const requestedPartner = searchParams.get(PARAM.partner) || "";
    const partner = PARTNER_OPTIONS.some((option) => option.id === requestedPartner) ? requestedPartner : "";

    // COD Watch shows no Cancelled tab. Resolving the tab against the list this page
    // actually renders — rather than trusting the URL — means ?tab=cancelled cannot smuggle
    // cancelled shipments in through a shared or edited link.
    const tabs = codOnly ? COD_TABS : QUEUE_TABS;
    const requestedTab = searchParams.get(PARAM.tab);
    const tab = tabs.some((entry) => entry.id === requestedTab) ? requestedTab : tabs[0].id;

    const query = useDeliveries({
        page,
        size: LEGACY_DEFAULT_PAGE_SIZE,
        search,
        tab,
        isCod: codOnly,
        deliveryPartnerId: partner,
    });

    const { AddopenModal, AddcloseModal } = useAddModalMain();

    // "View Images" is presentation-only, so it is handled here rather than in
    // useDeliveryActions, which exists for calls that hit the API.
    const openBoxImages = React.useCallback(
        (delivery) => {
            AddopenModal(() => <BoxImagesModal delivery={delivery} onClose={AddcloseModal} />);
        },
        [AddopenModal, AddcloseModal]
    );

    // Attach or replace open-box photos after the fact. `query.refetch` so the row's
    // View Images action and the stored names reflect the change without a reload.
    const openAddBoxImages = React.useCallback(
        (delivery) => {
            AddopenModal(() => (
                <AddBoxImagesModal delivery={delivery} onClose={AddcloseModal} onSaved={query.refetch} />
            ));
        },
        [AddopenModal, AddcloseModal, query.refetch]
    );

    // Read-only: the note is authored on the order edit page, not here.
    const openExtraItems = React.useCallback(
        (delivery) => {
            AddopenModal(() => <DeliveryExtraItemsModal delivery={delivery} onClose={AddcloseModal} />);
        },
        [AddopenModal, AddcloseModal]
    );

    // Track opens the SAME timeline modal the legacy /delivery page opens. It fetches
    // GET /delivery/track/:id itself on mount, so nothing is fetched here.
    const openTracking = React.useCallback(
        (delivery) => {
            AddopenModal(() => <DeliveryTrackingModal deliveryData={delivery} onClose={AddcloseModal} />);
        },
        [AddopenModal, AddcloseModal]
    );

    // Add / correct the AWB + tracking link on a manual courier delivery after booking.
    const openTrackingDetails = React.useCallback(
        (delivery) => {
            AddopenModal(() => (
                <TrackingDetailsModal delivery={delivery} onClose={AddcloseModal} onSaved={query.refetch} />
            ));
        },
        [AddopenModal, AddcloseModal, query.refetch]
    );

    const { handle, busy, bulk, bulkBusy } = useDeliveryActions({ onChanged: query.refetch });

    const runAction = React.useCallback(
        (action, delivery) => {
            if (action === "viewImages") return openBoxImages(delivery);
            if (action === "addImages") return openAddBoxImages(delivery);
            if (action === "extraItems") return openExtraItems(delivery);
            if (action === "track") return openTracking(delivery);
            if (action === "tracking") return openTrackingDetails(delivery);
            return handle(action, delivery);
        },
        [handle, openBoxImages, openAddBoxImages, openExtraItems, openTracking, openTrackingDetails]
    );

    const rows = useMemo(() => query.data?.deliveries ?? [], [query.data]);
    const pagination = query.data?.pagination ?? { page, size: LEGACY_DEFAULT_PAGE_SIZE, total: 0 };

    // Row selection for the bulk actions, scoped to the CURRENT PAGE like the legacy
    // /delivery page. Cleared whenever the tab, page or search changes, and pruned (not
    // cleared) when rows change under it, so a background refetch does not throw away a
    // selection that is still on screen while a row that just left the queue drops out.
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [showBulkModal, setShowBulkModal] = useState(false);

    useEffect(() => {
        setSelectedIds(new Set());
    }, [tab, page, search, partner, codOnly]);

    useEffect(() => {
        setSelectedIds((prev) => {
            const visible = new Set(rows.map((row) => row.id));
            const next = new Set([...prev].filter((id) => visible.has(id)));
            return next.size === prev.size ? prev : next;
        });
    }, [rows]);

    const selectedRows = useMemo(() => rows.filter((row) => selectedIds.has(row.id)), [rows, selectedIds]);
    const allSelected = rows.length > 0 && selectedRows.length === rows.length;

    const toggleRow = React.useCallback((id) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }, []);

    const toggleAll = React.useCallback(() => {
        setSelectedIds(allSelected ? new Set() : new Set(rows.map((row) => row.id)));
    }, [allSelected, rows]);

    const runBulk = async (fn) => {
        if (await fn(selectedRows)) setSelectedIds(new Set());
    };

    const setParam = (key, value, { resetPage = true } = {}) => {
        const next = new URLSearchParams(searchParams);
        if (value === "" || value == null) next.delete(key);
        else next.set(key, String(value));
        if (resetPage) next.delete(PARAM.page);
        setSearchParams(next, { replace: true });
    };

    // Spec §8 — these are computed over the CURRENT PAGE, not the whole queue, and the
    // labels say so. The prototype held all 12 mock rows in memory; the real list is
    // paginated at 50, so a global figure would need its own count queries. Deliberately not
    // implied here.
    const summary = useMemo(() => {
        const open = rows.filter(
            (row) => row.status !== "DELIVERED" && row.status !== "CANCELLED"
        );
        return {
            open: open.length,
            errors: rows.filter((row) => row.status === "ERROR").length,
            labelsPending: open.filter((row) => !row.courierDetails?.labelUrl).length,
            codExposure: open.reduce(
                (sum, row) => sum + (isCodDelivery(row) ? Number(codAmount(row)) || 0 : 0),
                0
            ),
        };
    }, [rows]);

    const columns = useMemo(
        () => [
            {
                key: "select",
                label: (
                    <input
                        type="checkbox"
                        aria-label="Select all on this page"
                        checked={allSelected}
                        ref={(el) => {
                            if (el) el.indeterminate = selectedRows.length > 0 && !allSelected;
                        }}
                        onChange={toggleAll}
                        className="h-4 w-4 cursor-pointer accent-[#00A48B]"
                    />
                ),
                render: (row) => (
                    <input
                        type="checkbox"
                        aria-label={`Select delivery ${row.id}`}
                        checked={selectedIds.has(row.id)}
                        onChange={() => toggleRow(row.id)}
                        className="h-4 w-4 cursor-pointer accent-[#00A48B]"
                    />
                ),
            },
            { key: "id", label: "#" },
            { key: "name", label: "Name", wrap: true, render: (row) => <NamePhoneCell delivery={row} /> },
            { key: "userOrderId", label: "User Order ID" },
            {
                key: "deliveryPartnerUniqueId",
                label: "Delivery Partner ID",
                render: (row) => {
                    if (isAwbPending(row)) {
                        return (
                            <span className="inline-flex items-center rounded-full bg-amber-50 px-2 py-1 text-xs font-medium text-amber-700 ring-1 ring-inset ring-amber-600/20">
                                AWB pending
                            </span>
                        );
                    }
                    return row.deliveryPartnerUniqueId || "—";
                },
            },
            { key: "deliveryPartner", label: "Delivery Partner", render: (row) => partnerName(row) },
            { key: "status", label: "Status", render: (row) => <StatusPill status={row.status} /> },
            {
                key: "pickupDate",
                label: "Pickup Date",
                render: (row) => (row.pickupDate ? formatShortDate(row.pickupDate) : "—"),
            },
            { key: "isCod", label: "Is COD", render: (row) => <CodChip isCod={isCodDelivery(row)} /> },
            {
                key: "codAmount",
                label: "COD Amount",
                align: "right",
                render: (row) => (isCodDelivery(row) ? formatCurrency(codAmount(row)) : "—"),
            },
            {
                key: "action",
                label: "Action",
                wrap: true,
                render: (row) => {
                    const { primary, utilities } = deliveryActions(row, { activeTab: tab });
                    const disabled = busy === row.id;
                    if (!primary && utilities.length === 0) return "—";
                    return (
                        <div className="flex min-w-[150px] flex-col gap-1">
                            {primary && (
                                <button
                                    type="button"
                                    disabled={disabled}
                                    onClick={() => runAction(primary.action, row)}
                                    className={`rounded-lg px-3 py-2 font-sans text-sm font-semibold text-white-50 transition-colors disabled:opacity-60 ${
                                        primary.tone === "danger"
                                            ? "bg-red-500 hover:bg-red-600"
                                            : "bg-[#00A48B] hover:bg-[#008f79]"
                                    }`}
                                >
                                    {disabled ? "Working…" : primary.label}
                                </button>
                            )}
                            {utilities.length > 0 && (
                                <div className="flex flex-wrap gap-1">
                                    {utilities.map((entry) => (
                                        <button
                                            key={entry.action}
                                            type="button"
                                            disabled={disabled}
                                            onClick={() => runAction(entry.action, row)}
                                            className={`rounded-md px-2 py-1 font-sans text-xs font-medium transition-colors disabled:opacity-60 ${
                                                entry.tone === "danger"
                                                    ? "text-red-600 hover:bg-red-50"
                                                    : "text-[var(--ops-carbon-500)] hover:bg-[var(--ops-carbon-100)]"
                                            }`}
                                        >
                                            {entry.label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                },
            },
        ],
        [tab, busy, runAction, allSelected, selectedRows.length, selectedIds, toggleAll, toggleRow]
    );

    const filters = (
        <div className="flex flex-col gap-3">
            <div className="flex gap-2 border-b border-[var(--ops-carbon-200)]">
                {tabs.map((entry) => (
                    <button
                        key={entry.id}
                        type="button"
                        onClick={() => setParam(PARAM.tab, entry.id)}
                        className={`-mb-px border-b-2 px-4 py-2 font-sans text-sm font-medium transition-colors ${
                            tab === entry.id
                                ? "border-[#00A48B] text-[#00A48B]"
                                : "border-transparent text-[var(--ops-carbon-500)] hover:text-[var(--ops-carbon-900)]"
                        }`}
                    >
                        {entry.label}
                    </button>
                ))}
            </div>

            <div className="flex flex-wrap items-center gap-2">
                <button
                    type="button"
                    onClick={() => query.refetch()}
                    className="rounded-lg bg-[#00A48B] px-4 py-2 font-sans text-sm font-semibold text-white-50 hover:bg-[#008f79]"
                >
                    Refresh
                </button>
                {/* Selection buttons appear only once something is ticked, same as legacy.
                    Generate Labels is hidden on Cancelled (nothing there is labelable) and
                    Mark Delivered is New-only, matching the per-row Mark Delivered rule. */}
                {selectedRows.length > 0 && tab !== "cancelled" && (
                    <button
                        type="button"
                        disabled={Boolean(bulkBusy)}
                        onClick={() => runBulk(bulk.generateLabels)}
                        className="rounded-lg bg-[#00A48B] px-4 py-2 font-sans text-sm font-semibold text-white-50 hover:bg-[#008f79] disabled:opacity-60"
                    >
                        {bulkBusy === "labels" ? "Generating…" : `Generate Labels (${selectedRows.length})`}
                    </button>
                )}
                {selectedRows.length > 0 && tab === "new" && (
                    <button
                        type="button"
                        disabled={Boolean(bulkBusy)}
                        onClick={() => runBulk(bulk.markDelivered)}
                        className="rounded-lg bg-emerald-600 px-4 py-2 font-sans text-sm font-semibold text-white-50 hover:bg-emerald-700 disabled:opacity-60"
                    >
                        {bulkBusy === "delivered" ? "Marking…" : `Mark Delivered (${selectedRows.length})`}
                    </button>
                )}
                {/* Mark delivered by pasted IDs (delivery / order / partner reference) —
                    the legacy modal, reused as-is, so both pages share one implementation. */}
                <button
                    type="button"
                    onClick={() => setShowBulkModal(true)}
                    className="rounded-lg bg-emerald-600 px-4 py-2 font-sans text-sm font-semibold text-white-50 hover:bg-emerald-700"
                >
                    Bulk Mark Delivered
                </button>
                <LogisticsSearchInput value={search} onSearch={(term) => setParam(PARAM.search, term)} />
                <select
                    aria-label="Filter by delivery partner"
                    value={partner}
                    onChange={(event) => setParam(PARAM.partner, event.target.value)}
                    className="rounded-lg border border-[var(--ops-carbon-200)] bg-white px-3 py-2 font-sans text-sm"
                >
                    <option value="">All delivery partners</option>
                    {PARTNER_OPTIONS.map((option) => (
                        <option key={option.id} value={option.id}>
                            {option.label}
                        </option>
                    ))}
                </select>
                <span className="text-sm text-[var(--ops-carbon-500)]">
                    {pagination.total} {pagination.total === 1 ? "shipment" : "shipments"}
                </span>
            </div>

            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                <SummaryCard label="Open Shipments" value={summary.open} hint="On this page" />
                <SummaryCard label="Error Queue" value={summary.errors} hint="On this page" />
                <SummaryCard label="Labels Pending" value={summary.labelsPending} hint="On this page" />
                <SummaryCard
                    label="COD Exposure"
                    value={formatCurrency(summary.codExposure)}
                    hint="Open COD on this page"
                />
            </div>
        </div>
    );

    return (
        <>
        <ListPage
            title={codOnly ? "COD Watch" : "Delivery Queue"}
            subtitle={
                codOnly
                    ? "Cash-on-delivery shipments only. Same queue, same actions."
                    : "Shipments by queue state. One recommended action per row."
            }
            filters={filters}
            columns={columns}
            rows={rows}
            query={query}
            pagination={pagination}
            onPageChange={(next) => setParam(PARAM.page, next, { resetPage: false })}
            emptyTitle={codOnly ? "No COD shipments here" : "No shipments in this queue"}
            emptyHint="Try another tab, or clear the search."
            testId={codOnly ? "logistics-cod" : "logistics-queue"}
        />
        {showBulkModal && (
            <BulkMarkDeliveredModal onClose={() => setShowBulkModal(false)} onSuccess={query.refetch} />
        )}
        </>
    );
}

function SummaryCard({ label, value, hint }) {
    return (
        <div className="rounded-2xl border border-[var(--ops-carbon-200)] bg-[var(--ops-carbon-25)] p-4">
            <div className="text-xs font-medium uppercase tracking-wide text-[var(--ops-carbon-500)]">{label}</div>
            <div className="mt-1 text-2xl font-bold text-[var(--ops-carbon-900)]">{value}</div>
            <div className="mt-1 text-xs text-[var(--ops-carbon-500)]">{hint}</div>
        </div>
    );
}
SummaryCard.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    hint: PropTypes.string,
};

DeliveryQueuePage.propTypes = { codOnly: PropTypes.bool };
DeliveryQueuePage.defaultProps = { codOnly: false };

export default DeliveryQueuePage;
